import React from 'react';
import styled from 'styled-components';
import { Slider } from './Slider';
import { formatIndianNumber } from '../utils/formatCurrency';

const InputRow = styled.div`
  margin-bottom: 20px;
`;

const InputTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  gap: 12px;
`;

const Label = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xsm};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  font-family: inherit;
  flex-shrink: 0;
  max-width: 244px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 150px;
    font-size: 14px;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  width: 288px;
  position: relative;

  @media (max-width: 768px) {
    width: 150px;
    min-width: 140px;
  }
`;

const StyledInput = styled.input<{ showClear?: boolean; hasPrefix?: boolean }>`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  padding-left: ${(props) => (props.hasPrefix ? '28px' : props.theme.spacing.sm)};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  font-family: inherit;
  width: 100%;
  flex: 1;

  &:focus {
    outline: none;
  }

  &::-webkit-search-cancel-button {
    ${(props) =>
      props.showClear
        ? `
      -webkit-appearance: none;
      appearance: none;
      height: 16px;
      width: 16px;
      border-radius: 50%;
      background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23666'%3E%3Cpath d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z'/%3E%3C/svg%3E") no-repeat center;
      background-size: 12px 12px;
      cursor: pointer;
      margin-left: 4px;
    `
        : `
      display: none;
    `}
  }

  &::-webkit-inner-spin-button,
  ::-webkit-outer-spin-button {
    ${(props) =>
      props.showClear
        ? `
      -webkit-appearance: none;
      margin: 0;
    `
        : `
      -webkit-appearance: none;
      cursor: pointer;
      display: block;
      width: 8px;
      height: 32px;
      position: relative;
      background: url("data:image/svg+xml,%3Csvg width='8' height='14' viewBox='0 0 8 14' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4 0L7.4641 4H0.535898L4 0Z' fill='%23666666'/%3E%3Cpath d='M4 14L0.535898 10L7.4641 10L4 14Z' fill='%23666666'/%3E%3C/svg%3E")
        no-repeat center center;
      opacity: 1;
      margin-right: 4px;
    `}
  }
`;

const InputContainer = styled.div<{ showClear?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding-right: ${(props) => (props.showClear ? '4px' : '0')};

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const CurrencySymbol = styled.span`
  position: absolute;
  left: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
`;

interface InputFieldProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  sliderConfig?: {
    min: number;
    max: number;
    step: number;
  };
  showClear?: boolean;
  defaultValue?: number;
  prefix?: string;
}

export const InputField: React.FC<InputFieldProps> = ({
  label,
  value,
  onChange,
  sliderConfig,
  showClear = false,
  defaultValue = 0,
  prefix = '',
}) => {
  return (
    <InputRow>
      <InputTopRow>
        <Label>{label}</Label>
        <InputWrapper>
          <InputContainer>
            {prefix && <CurrencySymbol>{prefix}</CurrencySymbol>}
            <StyledInput
              type={showClear ? 'search' : 'number'}
              value={showClear ? formatIndianNumber(value) : value}
              onChange={(e) => {
                if (showClear) {
                  const cleanValue = e.target.value.replace(/,/g, '');
                  const newValue = cleanValue === '' ? defaultValue : Number(cleanValue);
                  onChange(newValue);
                } else {
                  const newValue = e.target.value === '' ? defaultValue : Number(e.target.value);
                  onChange(newValue);
                }
              }}
              showClear={showClear}
              hasPrefix={!!prefix}
            />
          </InputContainer>
        </InputWrapper>
      </InputTopRow>
      {sliderConfig && (
        <Slider
          min={sliderConfig.min}
          max={sliderConfig.max}
          step={sliderConfig.step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
        />
      )}
    </InputRow>
  );
};
